/**
 * coπ's server side, from the app:
 *  - askCopiAi — what spoken words (or a recording) meant, for words the free
 *    rules in lib/copi-commands + lib/copi-media couldn't place. Sends the
 *    words and the catalogue's NAMES (no ids, nothing private).
 *  - loadKnowledge / teachCopi / rememberCopi / logCopiMiss — what coπ has
 *    learned: phrases taught via "What did you mean?" (2026-10-31), AI answers
 *    nobody undid and name spellings from the nightly review (2026-11-01),
 *    and the misses it writes down.
 *  - countCopiLayer — which layer answered, batched (aggregate counts only).
 */

import type { User } from 'firebase/auth';
import type { CopiLibrary } from '@/lib/copi-media';
import { phraseKey, type CopiPage } from '@/lib/copi-commands';
import { cleanCopiAction, copiLang, copiMemoryKey, type CopiAiAction, type CopiLayer } from '@/lib/copi-learning';

export type { CopiAiAction } from '@/lib/copi-learning';

const uniq = (xs: string[]) => [...new Set(xs.map(x => x.trim()).filter(Boolean))];

async function headers(user: User | null): Promise<Record<string, string>> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  try { const token = await user?.getIdToken(); if (token) h.Authorization = `Bearer ${token}`; } catch { /* as a visitor */ }
  return h;
}

/** Words, or a recording (base64 16 kHz mono WAV) — then `heard` is what was said. */
export async function askCopiAi(
  input: { text: string } | { wav: string },
  lib: CopiLibrary, pages: CopiPage[], user: User | null,
): Promise<{ action: CopiAiAction | null; heard?: string } | null> {
  const catalogue = {
    pages: pages.map(p => ({ href: p.href, name: p.names.join(' / ') })),
    artists: uniq([...(lib.artists || []).map(a => a.name), ...lib.tracks.map(t => t.artist)]),
    releases: uniq(lib.releases.map(r => r.title)),
    tracks: uniq(lib.tracks.map(t => t.title)),
    genres: uniq(lib.tracks.flatMap(t => t.genres)),
    videos: uniq(lib.videos.map(v => v.title)),
    playlists: uniq(lib.playlists.map(p => p.title)),
  };
  const body = 'text' in input
    ? { text: input.text, catalogue }
    : { audio: { mime: 'audio/wav', data: input.wav }, catalogue };
  try {
    const res = await fetch('/api/ai/copi', { method: 'POST', headers: await headers(user), body: JSON.stringify(body) });
    if (!res.ok) return null;
    const data = await res.json();
    const action = data?.action && typeof data.action.action === 'string' ? (data.action as CopiAiAction) : null;
    return { action, heard: typeof data?.heard === 'string' ? data.heard : undefined };
  } catch {
    return null;
  }
}

export interface CopiKnowledge {
  /** phrase key → what it means, as text ("What did you mean?"). */
  pairs: Map<string, string>;
  /** `${lang}|${key}` → a remembered action. */
  memory: Map<string, CopiAiAction>;
  /** spoken key → catalogue name ("leaf nut" → Leafnuts). */
  aliases: Map<string, string>;
}
type Wire = { pairs?: Record<string, string>; memory?: { k: string; l: string; a: unknown }[]; aliases?: { k: string; n: string }[] };

const DEVICE_KEY = 'bb-copi-memory';
/** Visitors have no account — what coπ learned from them stays on their device. */
function deviceMemory(): { k: string; l: string; a: unknown }[] {
  try { const raw = localStorage.getItem(DEVICE_KEY); const v = raw ? JSON.parse(raw) : []; return Array.isArray(v) ? v : []; } catch { return []; }
}
function addTo(k: CopiKnowledge, w: Wire | null) {
  if (!w) return;
  Object.entries(w.pairs || {}).forEach(([key, meant]) => { if (typeof meant === 'string') k.pairs.set(key, meant); });
  (w.memory || []).forEach(m => {
    const a = cleanCopiAction(m?.a);
    if (!a || a.action === 'unknown' || typeof m.k !== 'string') return;
    k.memory.set(copiMemoryKey(String(m.l || '*'), m.k), a);
    if (!k.memory.has(copiMemoryKey('*', m.k))) k.memory.set(copiMemoryKey('*', m.k), a);
  });
  (w.aliases || []).forEach(a => { if (a?.k && a?.n) k.aliases.set(a.k, a.n); });
}

let knowledge: { uid: string; data: Promise<CopiKnowledge> } | null = null;

/** What coπ knows — everyone's (CDN-cached) plus your own (yours wins). Once per visit. */
export function loadKnowledge(user: User | null): Promise<CopiKnowledge> {
  const uid = user?.uid || '';
  if (knowledge && knowledge.uid === uid) return knowledge.data;
  const data = (async () => {
    const k: CopiKnowledge = { pairs: new Map(), memory: new Map(), aliases: new Map() };
    const get = async (url: string, auth: boolean): Promise<Wire | null> => {
      try { const r = await fetch(url, auth ? { headers: await headers(user) } : undefined); return r.ok ? await r.json() : null; } catch { return null; }
    };
    const [global, mine] = await Promise.all([
      get('/api/copi/learn?scope=global', false),
      uid ? get('/api/copi/learn?scope=mine', true) : Promise.resolve<Wire>({ memory: deviceMemory() }),
    ]);
    addTo(k, global);
    addTo(k, mine);
    return k;
  })();
  knowledge = { uid, data };
  return data;
}

/** "By <heard> I meant <meant>" — remembered at once on this device, and on the server. */
export async function teachCopi(heard: string, meant: string, user: User | null, page: string, locale = ''): Promise<void> {
  const k = await loadKnowledge(user);
  k.pairs.set(phraseKey(heard), meant);
  try {
    await fetch('/api/copi/learn', { method: 'POST', headers: await headers(user), body: JSON.stringify({ heard, meant, page, locale }) });
  } catch { /* still remembered for this visit */ }
}

/** An AI answer nobody undid — coπ answers these words from memory from now on. */
export async function rememberCopi(heard: string, action: CopiAiAction, user: User | null, locale: string): Promise<void> {
  const lang = copiLang(locale);
  const key = phraseKey(heard);
  if (!key) return;
  const k = await loadKnowledge(user);
  k.memory.set(copiMemoryKey(lang, key), action);
  if (!user) {
    try {
      const list = deviceMemory().filter(m => !(m.k === key && m.l === lang));
      list.push({ k: key, l: lang, a: action });
      localStorage.setItem(DEVICE_KEY, JSON.stringify(list.slice(-100)));
    } catch { /* storage blocked — remembered for this visit */ }
    return;
  }
  try {
    await fetch('/api/copi/remember', { method: 'POST', headers: await headers(user), body: JSON.stringify({ heard, action, locale }) });
  } catch { /* remembered for this visit */ }
}

export function logCopiMiss(heard: string, reason: 'unplaced' | 'notFound' | 'notHeard' | 'wrong' | 'contradicted', user: User | null, page: string, locale: string, result = '') {
  void headers(user).then(h => fetch('/api/copi/miss', { method: 'POST', headers: h, body: JSON.stringify({ heard, reason, page, locale, result }) })).catch(() => {});
}

// Which layer answered — counted here and sent in one small batch.
const pending: Partial<Record<CopiLayer, number>> = {};
let pendingLocale = 'en';
let flushTimer: ReturnType<typeof setTimeout> | null = null;
function flushCounts(beacon = false) {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  const counts = { ...pending };
  if (!Object.keys(counts).length) return;
  (Object.keys(pending) as CopiLayer[]).forEach(l => { delete pending[l]; });
  const body = JSON.stringify({ counts, locale: pendingLocale });
  try {
    if (beacon && navigator.sendBeacon) navigator.sendBeacon('/api/copi/stat', new Blob([body], { type: 'application/json' }));
    else void fetch('/api/copi/stat', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  } catch { /* counts are best effort */ }
}
let hideHooked = false;
export function countCopiLayer(layer: CopiLayer, locale: string) {
  if (typeof window === 'undefined') return;
  if (locale !== pendingLocale) flushCounts();
  pendingLocale = locale;
  pending[layer] = (pending[layer] || 0) + 1;
  if (!flushTimer) flushTimer = setTimeout(() => flushCounts(), 20_000);
  if (!hideHooked) {
    hideHooked = true;
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') flushCounts(true); });
  }
}
